import { useCallback, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
    AccountCircle,
    BarChart,
    Chat,
    ChevronLeft,
    ChevronRight,
    CreditCard,
    DarkMode,
    HelpOutline,
    LightMode,
    List as ListIcon,
    Logout,
    Menu as MenuIcon,
    Preview,
    Search,
    Settings,
    SpaceDashboard,
} from '@mui/icons-material';
import { useLoginStatusStore } from "../../../../config/user.stores";
import resetAllStores from "../../../../hooks/resetAllStores";
import LandingPage from '../LandingPage';
import { AppBar, DrawerHeader, Main } from "./styles";
import { useUser } from "../../../../hooks/useUser";
import { toggleAppearence } from "../../../../hooks/appearance";
import { drawerWidth } from "../../../../config/constants";
import logoRed from '../../../../assets/logo-red.png';
import logoWhite from '../../../../assets/logo-white.png';

type NavbarType = {
    appearence: string;
    setAppearence: (v: string) => void;
}

const Navbar = ({ appearence, setAppearence }: NavbarType) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const profileOpen = Boolean(anchorEl);
    const userLoggedIn = useLoginStatusStore((state) => state.userLoggedIn);

    const user = useUser();

    const drawerItems = useCallback(() => {
        if (user?.category === 0) {
            return [
                {"text": "Dashboard", "link": "/admin/", "icon": <SpaceDashboard />},
                {"text": "Manage Campaigns", "link": "/admin/campaigns", "icon": <ListIcon />},
                {"text": "Manage Templates", "link": "/admin/templates", "icon": <Preview />},
                {"text": "View Payments", "link": "/admin/payments", "icon": <CreditCard />},
                {"text": "Settings", "link": "/settings", "icon": <Settings />},
            ];
        } else if (user?.category === 1) {
            return [
                {"text": "Dashboard", "link": "/", "icon": <SpaceDashboard />},
                {"text": "Manage Campaigns", "link": "/campaigns", "icon": <ListIcon />},
                {"text": "Analytics", "link": "/analytics", "icon": <BarChart />},
                {"text": "Settings", "link": "/settings", "icon": <Settings />},
            ];
        } else if (user?.category === 2 || user?.category === 3) {
            return [
                {"text": "Dashboard", "link": "/", "icon": <SpaceDashboard />},
                {"text": "Search Players", "link": "/search", "icon": <Search />},
                {"text": "Messages", "link": "/messages", "icon": <Chat />},
                {"text": "Settings", "link": "/settings", "icon": <Settings />},
            ];
        }
        return [];
    }, [user?.category]);

    const handleProfileOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }
    const handleProfileClose = () => {
        setAnchorEl(null);
    }

    const handleThemeChange = () => {
        const newAppearence = toggleAppearence();
        setAppearence(newAppearence);
    }
    const handleRedirect = (page: string) => {
        handleProfileClose();
        navigate(page);
    }

    const handleDrawerOpen = () => {
        setDrawerOpen(true);
    };
    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const logout = () => {
        resetAllStores();
        handleProfileClose();
        navigate('/');
    };

    return (
        <Box sx={{ display: 'flex' }}>
            { userLoggedIn ? (
                <>
                <AppBar position="fixed" open={drawerOpen}>
                    <Toolbar sx={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}>
                        <IconButton
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            edge="start"
                            sx={[
                                { mr: 2, color: "#FFFFFF" },
                                drawerOpen && { display: 'none' },
                            ]}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Box display="flex" width="100%" justifyContent="space-between" alignItems="center">
                            <Link to="/" style={{ display: "flex" }}>
                                <img
                                    src={appearence === "dark" ? logoRed : logoWhite}
                                    alt="Statrat logo"
                                    height="58px"
                                />
                            </Link>
                            <IconButton
                                aria-controls={profileOpen ? 'profile-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={profileOpen ? 'true' : undefined}
                                sx={{ color: "#FFFFFF" }}
                                onClick={handleProfileOpen}
                            >
                                <AccountCircle fontSize="large"/>
                            </IconButton>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Drawer
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                        },
                    }}
                    variant="persistent"
                    anchor="left"
                    open={drawerOpen}
                >
                    <DrawerHeader>
                        <IconButton onClick={handleDrawerClose} sx={{ marginTop: "0.5rem" }}>
                            {theme.direction === 'ltr' ? <ChevronLeft /> : <ChevronRight />}
                        </IconButton>
                    </DrawerHeader>
                    <List>
                        {drawerItems().map((item) => (
                            <Link 
                                key={item.text}
                                to={item.link}
                                style={{ textDecoration: "none", color: appearence === "dark" ? "#FFFFFF" : "#000000" }}
                            >
                                <ListItem disablePadding>
                                    <ListItemButton>
                                        <ListItemIcon>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText primary={item.text} />
                                    </ListItemButton>
                                </ListItem>
                            </Link>
                        ))}
                    </List>
                </Drawer>
                <Menu
                    anchorEl={anchorEl}
                    id="profile-menu"
                    open={profileOpen}
                    onClose={handleProfileClose}
                    slotProps={{ paper: { elevation: 0 } }}
                >
                    <Typography sx={{ pt: 1, pl: 2 }}>{user.businessName}</Typography>
                    <Typography sx={{ pr: 2, pl: 2, pb: 1 }}>{user.email}</Typography>
                    <Divider />
                    <MenuItem onClick={() => { handleRedirect("/settings/profile") }} sx={{ p: 1, pl: 2 }}>
                        <AccountCircle sx={{ mr: "1rem" }} /> Your profile
                    </MenuItem>
                    <MenuItem onClick={() => { handleRedirect("/settings/billing") }} sx={{ p: 1, pl: 2 }}>
                        <CreditCard sx={{ mr: "1rem" }} /> Billing
                    </MenuItem>
                    <MenuItem onClick={() => { handleRedirect("/settings/help") }} sx={{ p: 1, pl: 2 }}>
                        <HelpOutline sx={{ mr: "1rem" }} /> Help
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleThemeChange} sx={{ p: 1, pl: 2 }}>
                        {appearence === "dark" ? (
                            <LightMode sx={{ mr: "1rem" }} />
                        ) : (
                            <DarkMode sx={{ mr: "1rem" }} />
                        )}
                        Change Appearence
                    </MenuItem>
                    <MenuItem onClick={logout} sx={{ p: 1, pl: 2 }}>
                        <Logout sx={{ mr: "1rem" }} /> Logout
                    </MenuItem>
                </Menu>
                <Main open={drawerOpen}>
                    <DrawerHeader />
                    <Outlet />
                </Main>
                </>
            ) : (
                <LandingPage />
            )}
        </Box>
    )
}

export default Navbar;
